import React, { useState } from 'react';
import { NumberOfFaultsChart, DowntimeDurationOfFaultsChart, MachineStatusPerShiftChart } from './index';
import SubTabNav from '../common/SubTabNav';
import { styles, cx } from '../../styles';

interface FaultsChartContainerProps {
  machineSerialNo?: string;
  lineId?: string; // Line ID for line-level aggregated data
  shifts?: string[];
  className?: string;
  activeSubTab?: 'number' | 'duration' | 'status';
  onActiveSubTabChange?: (tab: 'number' | 'duration' | 'status') => void;
  aggType?: 'number' | 'percent';
  onAggTypeChange?: (type: 'number' | 'percent') => void;
}

const FaultsChartContainer: React.FC<FaultsChartContainerProps> = ({ 
  machineSerialNo, 
  lineId,
  shifts, 
  className = '',
  activeSubTab,
  onActiveSubTabChange,
  aggType: externalAggType,
  onAggTypeChange
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<'number' | 'duration' | 'status'>('number');
  const [internalAggType, setInternalAggType] = useState<'number' | 'percent'>('number');
  
  // Use external state if provided, otherwise use internal state
  const activeTab = activeSubTab !== undefined ? activeSubTab : internalActiveTab;
  const setActiveTab = onActiveSubTabChange || setInternalActiveTab;
  const aggType = externalAggType !== undefined ? externalAggType : internalAggType;
  const setAggType = onAggTypeChange || setInternalAggType;

  const tabs = [
    { key: 'number', label: 'Number of Faults' },
    { key: 'duration', label: 'Downtime Duration of Faults' },
    { key: 'status', label: 'Machine Status per Shift' }
  ];

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Subtab navigation */}
      <SubTabNav
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as 'number' | 'duration' | 'status')}
      />

      {/* Aggregation type selector - only show for status tab */}
      {activeTab === 'status' && (
        <div className={styles.tabComponents.toggleGroup.container}>
          <div className={styles.tabComponents.toggleGroup.wrapper}>
            <button
              onClick={() => setAggType('number')}
              className={cx(
                styles.tabComponents.toggleGroup.button.base,
                aggType === 'number' ? styles.tabComponents.toggleGroup.button.active : styles.tabComponents.toggleGroup.button.inactive
              )}
            >
              in Number
            </button>
            <button
              onClick={() => setAggType('percent')}
              className={cx(
                styles.tabComponents.toggleGroup.button.base,
                aggType === 'percent' ? styles.tabComponents.toggleGroup.button.active : styles.tabComponents.toggleGroup.button.inactive
              )}
            >
              in Percent
            </button>
          </div>
        </div>
      )}

      {/* Chart content based on active tab */}
      {activeTab === 'number' ? (
        <NumberOfFaultsChart 
          machineSerialNo={machineSerialNo}
          lineId={lineId}
          shifts={shifts}
        />
      ) : activeTab === 'duration' ? (
        <DowntimeDurationOfFaultsChart 
          machineSerialNo={machineSerialNo}
          lineId={lineId}
          shifts={shifts}
        />
      ) : (
        <MachineStatusPerShiftChart 
          machineSerialNo={machineSerialNo}
          lineId={lineId}
          shifts={shifts}
          aggType={aggType}
        />
      )}
    </div>
  );
};

export default FaultsChartContainer;
